import React from "react";
import { ShieldCheck, Clock, Lock, Sparkles } from "lucide-react";
import { useCountry } from "../../contexts/CountryContext";
import { getCountryConfig } from "../../lib/country";

/**
 * Accurate, non-misleading provider trust badges.
 *
 * Trust messaging policy (Jun 2026 — locked):
 *   - ID Verified            (green)   → provider.is_id_verified === true
 *   - Verification Pending   (amber)   → provider.is_id_verified === false (and status='approved')
 *   - New Provider           (neutral) → provider.review_count === 0 (and status='approved')
 *
 * REMOVED (Jun 2026): "Carefully Reviewed" badge — it implied HandyHub
 * manually reviews every provider, which is not accurate. The ID Verified
 * badge is the single trust signal and appears only when identity documents
 * are confirmed. Customers should independently assess providers before
 * engaging services (see footer disclaimer).
 *
 * `size` controls icon size only ("sm" | "md").
 */
export const ProviderBadges = ({
  provider,
  size = "sm",
  showNew = true,
  className = "",
}) => {
  const { countryCode } = useCountry();
  if (!provider) return null;
  const iconSize = size === "md" ? 14 : 12;
  const isApproved = !provider.status || provider.status === "approved";
  // AU (for now): only the ID Verified badge is shown publicly.
  const showActivity = getCountryConfig(countryCode).showMarketplaceActivity !== false;

  return (
    <div className={`provider-trust-badges ${className}`} data-testid="provider-trust-badges">
      {provider.is_id_verified ? (
        <span className="trust-badge trust-badge-green" data-testid="badge-verified">
          <ShieldCheck size={iconSize} strokeWidth={2.5} />
          ID Verified
        </span>
      ) : isApproved && showActivity ? (
        <span className="trust-badge trust-badge-amber" data-testid="badge-verification-pending">
          <Clock size={iconSize} strokeWidth={2.5} />
          Verification Pending
        </span>
      ) : null}

      {showNew && showActivity && isApproved && (provider.review_count ?? 0) === 0 && (
        <span className="trust-badge trust-badge-neutral" data-testid="badge-new">
          <Sparkles size={iconSize} strokeWidth={2.5} />
          New Provider
        </span>
      )}
    </div>
  );
};

/** Standalone "Payments secured by Stripe" trust line. */
export const StripeSecuredBadge = ({ className = "" }) => (
  <span className={`trust-badge trust-badge-locked ${className}`} data-testid="badge-stripe-secured">
    <Lock size={12} strokeWidth={2.5} />
    Payments Secured by Stripe
  </span>
);

export default ProviderBadges;
